import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import type { AgentV2MessageAttachedFiles } from '@/agent-v2/store/reducer'
import {
  AgentV2ClientSurface,
  AgentV2ConversationDetailResponse,
  AgentV2ConversationSummary,
  AgentV2ServerEventType,
  type AgentV2TurnRequestPayload,
} from '@/agent-v2/store/schemas'
import type { AgentV2TurnFeedback } from '@/agent-v2/feedback/schemas'
import { setActiveConversationId } from '@/chat-common/store/chat-ux.slice'
import { UserFilesActions } from '@/user_files/files/store/slice'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { kNewConversationTitle } from '@/chat-common/conversation_list/constants'
import { mergeConversationDetail } from '@/agent-v2/conversations/utils/merge-conversation-detail'
import { DocxExportStatus, type DocxExportRead } from '@/agent-v2/docx-export/schemas'

// A retried start can return an export that has already ended; only these are still running.
const kOpenDocxExportStatuses = new Set([DocxExportStatus.QUEUED, DocxExportStatus.RUNNING])

// A detail-load failure is only terminal when the server says the conversation does not exist —
// anything else (500, network, schema) must stay retryable and must not render as not-found (ENG-7070)
export enum AgentV2DetailLoadError {
  NOT_FOUND = 'not_found',
  RETRYABLE = 'retryable',
  ADD_IN_ONLY = 'add_in_only',
}

export enum AgentV2ConversationDeletionStatus {
  DELETING = 'deleting',
  DELETED = 'deleted',
}

export enum AgentV2RunPhase {
  IDLE = 'idle',
  SUBMITTING = 'submitting',
  RUNNING = 'running',
  STOPPING = 'stopping',
  RECOVERING = 'recovering',
}

export type AgentV2RunLifecycle = {
  phase: AgentV2RunPhase
  turnId: string | null
  clientRequestId: string | null
  pendingRequest: AgentV2TurnRequestPayload | null
  admissionFallbackInFlight: boolean
  stopRequested: boolean
  recoveryGeneration: number
}

// Detail load-state lives only in detailLoadingByConversationId / detailErrorByConversationId (ENG-7073)
export type AgentV2Conversation = AgentV2ConversationSummary & {
  lastRefresh: number | null
}

export type AgentV2TitlePlaceholder = {
  /* The message sent while the conversation was untitled */
  text: string
  /* A title is being generated to replace it */
  titleGenerating: boolean
}

export type AgentV2FollowUpQuestionsState = {
  conversationId: string
  loading: boolean
  isError: boolean
  questions: string[]
}

type SidebarChatList = {
  ids: string[]
  loading: boolean
  loaded: boolean
  error: boolean
  lastRefresh: number | null
  requestId: string
}

export type AgentV2ConversationsState = {
  historyByConversationId: Record<string, { nextCursor: string | null; loading: boolean; error: boolean; requestId: string | null } | undefined>
  sidebarListsByMatterId: Record<string, SidebarChatList>
  allRequestedByMatterId: Record<string, boolean>
  fullListLoadedByMatterId: Record<string, boolean>
  lifecycleGeneration: number
  // The app host running this store. It submits turns as this surface and lists and opens only
  // conversations that originated here; the Outlook pane sets it once at mount.
  hostSurface: AgentV2ClientSurface
  conversations: Record<string, AgentV2Conversation>
  listedConversationIdsByMatterId: Record<string, string[]>
  // Accumulated server history (including references, for fidelity); citation resolution
  // never reads these — agentV2ReferencesState is the sole resolution owner.
  details: Record<string, AgentV2ConversationDetailResponse | undefined>
  feedbackByTurnId: Record<string, AgentV2TurnFeedback | null | undefined>
  followUpQuestionsByAssistantMessageId: Record<string, AgentV2FollowUpQuestionsState | undefined>
  initialLoadCompleteByMatterId: Record<string, boolean>
  lastRefreshByMatterId: Record<string, number | null>
  loadingByMatterId: Record<string, boolean>
  errorByMatterId: Record<string, boolean>
  requestIdByMatterId: Record<string, string>
  detailRequestIdByConversationId: Record<string, string>
  detailLoadingByConversationId: Record<string, boolean>
  detailErrorByConversationId: Record<string, AgentV2DetailLoadError | null>
  detailLastRefreshByConversationId: Record<string, number | null>
  deletionStatusByConversationId: Record<string, AgentV2ConversationDeletionStatus | undefined>
  // Transcript rendering has its own conversation-keyed slice. This lifecycle is the
  // shared run-status owner for navigation, the sidebar, and background REST recovery.
  runLifecycleByConversationId: Record<string, AgentV2RunLifecycle | undefined>
  // Session-only: cleared on viewing the chat, deletion, or authentication teardown.
  unreadResponseByConversationId: Record<string, boolean>
  // A stand-in for an untitled conversation's title until one is generated.
  titlePlaceholderByConversationId: Record<string, AgentV2TitlePlaceholder | undefined>
  // The files and writing sample each chat's latest turn was sent with, which every next turn sends again
  chatFilesByConversationId: Record<string, AgentV2MessageAttachedFiles | undefined>
}

const initialState: AgentV2ConversationsState = {
  historyByConversationId: {},
  lifecycleGeneration: 0,
  hostSurface: AgentV2ClientSurface.WEB,
  sidebarListsByMatterId: {},
  allRequestedByMatterId: {},
  fullListLoadedByMatterId: {},
  conversations: {},
  listedConversationIdsByMatterId: {},
  details: {},
  feedbackByTurnId: {},
  followUpQuestionsByAssistantMessageId: {},
  initialLoadCompleteByMatterId: {},
  lastRefreshByMatterId: {},
  loadingByMatterId: {},
  errorByMatterId: {},
  requestIdByMatterId: {},
  detailRequestIdByConversationId: {},
  detailLoadingByConversationId: {},
  detailErrorByConversationId: {},
  detailLastRefreshByConversationId: {},
  deletionStatusByConversationId: {},
  runLifecycleByConversationId: {},
  unreadResponseByConversationId: {},
  titlePlaceholderByConversationId: {},
  chatFilesByConversationId: {},
}

export const agentV2ConversationsSlice = createSlice({
  name: 'agentV2ConversationsState',
  initialState,
  reducers: {
    markResponseUnread: (state, action: PayloadAction<{ conversationId: string }>) => {
      if (state.deletionStatusByConversationId[action.payload.conversationId]) return
      state.unreadResponseByConversationId[action.payload.conversationId] = true
    },
    refreshConversationList: (_state, _action: PayloadAction<{ matterId: string; forceRefresh: boolean; sidebar?: boolean; all?: boolean }>) => {},
    fetchConversationDetail: (_state, _action: PayloadAction<{ conversationId: string; forceRefresh: boolean; treatNotFoundAsDeleted?: boolean }>) => {},
    fetchOlderConversationHistory: (_state, _action: PayloadAction<{ conversationId: string }>) => {},
    beginOlderHistoryLoad: (state, action: PayloadAction<{ conversationId: string; requestId: string }>) => {
      const history = state.historyByConversationId[action.payload.conversationId]
      if (!history || state.deletionStatusByConversationId[action.payload.conversationId]) return
      history.loading = true
      history.error = false
      history.requestId = action.payload.requestId
    },
    olderHistoryLoadFailed: (state, action: PayloadAction<{ conversationId: string }>) => {
      const history = state.historyByConversationId[action.payload.conversationId]
      if (!history) return
      history.loading = false
      history.error = true
      history.requestId = null
    },
    olderConversationHistoryReceived: (state, action: PayloadAction<AgentV2ConversationDetailResponse>) => {
      const detail = action.payload
      const conversationId = detail.conversation.id
      const existing = state.details[conversationId]
      if (!existing || state.deletionStatusByConversationId[conversationId]) return
      // An older page adds history only. Open exports stay as the newest-page reads set them: the
      // export poll reads that page and posts the result and notice, so an older page read before
      // an export started, or just after it ended, must not end tracking.
      state.details[conversationId] = {
        ...mergeConversationDetail(existing, detail),
        conversation: existing.conversation,
        open_docx_exports: existing.open_docx_exports,
      }
      for (const turn of detail.turns) state.feedbackByTurnId[turn.id] = turn.feedback
      state.historyByConversationId[conversationId] = { nextCursor: detail.next_cursor ?? null, loading: false, error: false, requestId: null }
    },
    generateConversationTitle: (_state, _action: PayloadAction<{ conversationId: string }>) => {},
    conversationTitleGenerationFailed: (state, action: PayloadAction<{ conversationId: string }>) => {
      const placeholder = state.titlePlaceholderByConversationId[action.payload.conversationId]
      if (placeholder) placeholder.titleGenerating = false
    },
    generateConversationFollowUpQuestions: (state, action: PayloadAction<{ conversationId: string; assistantMessageId: string }>) => {
      if (state.deletionStatusByConversationId[action.payload.conversationId]) return
      if (state.followUpQuestionsByAssistantMessageId[action.payload.assistantMessageId]) return
      state.followUpQuestionsByAssistantMessageId[action.payload.assistantMessageId] = {
        conversationId: action.payload.conversationId,
        loading: true,
        isError: false,
        questions: [],
      }
    },

    runSubmitted: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string; request: AgentV2TurnRequestPayload }>) => {
      const previousGeneration = state.runLifecycleByConversationId[action.payload.conversationId]?.recoveryGeneration ?? 0
      state.runLifecycleByConversationId[action.payload.conversationId] = {
        ...createRunLifecycle(AgentV2RunPhase.SUBMITTING, previousGeneration),
        clientRequestId: action.payload.clientRequestId,
        pendingRequest: action.payload.request,
      }
      if (state.conversations[action.payload.conversationId]?.title === kNewConversationTitle) {
        const placeholder = state.titlePlaceholderByConversationId[action.payload.conversationId]
        state.titlePlaceholderByConversationId[action.payload.conversationId] = {
          text: placeholder?.text ?? action.payload.request.input_text,
          titleGenerating: true,
        }
      }
    },
    runStarted: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string; turnId: string }>) => {
      const existing = state.runLifecycleByConversationId[action.payload.conversationId]
      if (existing?.clientRequestId && existing.clientRequestId !== action.payload.clientRequestId) return
      const previousGeneration = existing?.recoveryGeneration ?? 0
      const stopRequested = existing?.stopRequested ?? false
      state.runLifecycleByConversationId[action.payload.conversationId] = {
        ...createRunLifecycle(stopRequested ? AgentV2RunPhase.STOPPING : AgentV2RunPhase.RUNNING, previousGeneration),
        clientRequestId: action.payload.clientRequestId,
        turnId: action.payload.turnId,
        stopRequested,
      }
      markConversationActive(state, action.payload.conversationId)
    },
    runStopRequested: (state, action: PayloadAction<{ conversationId: string }>) => {
      const lifecycle = state.runLifecycleByConversationId[action.payload.conversationId]
      if (!lifecycle || (lifecycle.phase !== AgentV2RunPhase.SUBMITTING && lifecycle.phase !== AgentV2RunPhase.RUNNING)) return
      lifecycle.phase = AgentV2RunPhase.STOPPING
      lifecycle.stopRequested = true
    },
    runTerminalObserved: (state, action: PayloadAction<{ conversationId: string; turnId: string | null }>) => {
      const previousGeneration = state.runLifecycleByConversationId[action.payload.conversationId]?.recoveryGeneration ?? 0
      state.runLifecycleByConversationId[action.payload.conversationId] = {
        ...createRunLifecycle(AgentV2RunPhase.IDLE, previousGeneration),
        turnId: action.payload.turnId,
      }
    },
    runDidNotStart: (state, action: PayloadAction<{ conversationId: string }>) => {
      const previousGeneration = state.runLifecycleByConversationId[action.payload.conversationId]?.recoveryGeneration ?? 0
      state.runLifecycleByConversationId[action.payload.conversationId] = createRunLifecycle(AgentV2RunPhase.IDLE, previousGeneration)
      // A placeholder from a message that was sent stays; one from a message that never started is dropped.
      const placeholder = state.titlePlaceholderByConversationId[action.payload.conversationId]
      if (placeholder && state.conversations[action.payload.conversationId]?.has_activity) {
        placeholder.titleGenerating = false
      } else {
        delete state.titlePlaceholderByConversationId[action.payload.conversationId]
      }
    },
    beginRunRecovery: (state, action: PayloadAction<{ conversationId: string }>) => {
      startRunRecovery(state, action.payload.conversationId)
    },
    requestTurnAdmissionRecovery: (state, action: PayloadAction<{ conversationId: string }>) => {
      const lifecycle = state.runLifecycleByConversationId[action.payload.conversationId]
      if (lifecycle?.phase !== AgentV2RunPhase.RECOVERING || !lifecycle.pendingRequest || lifecycle.admissionFallbackInFlight) return
      lifecycle.admissionFallbackInFlight = true
    },
    turnAdmissionRecovered: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string; turnId: string }>) => {
      const lifecycle = state.runLifecycleByConversationId[action.payload.conversationId]
      if (lifecycle?.phase !== AgentV2RunPhase.RECOVERING || lifecycle.clientRequestId !== action.payload.clientRequestId) return
      lifecycle.admissionFallbackInFlight = false
      lifecycle.pendingRequest = null
      lifecycle.turnId = action.payload.turnId
      markConversationActive(state, action.payload.conversationId)
    },
    turnAdmissionRecoveryFailed: (state, action: PayloadAction<{ clientRequestId: string; conversationId: string }>) => {
      const lifecycle = state.runLifecycleByConversationId[action.payload.conversationId]
      if (lifecycle?.phase !== AgentV2RunPhase.RECOVERING || lifecycle.clientRequestId !== action.payload.clientRequestId) return
      lifecycle.admissionFallbackInFlight = false
    },

    setHostSurface: (state, action: PayloadAction<AgentV2ClientSurface>) => {
      state.hostSurface = action.payload
    },

    // Sign-out clears user data; the host running this store does not change.
    nullifyData: (state) => ({ ...initialState, hostSurface: state.hostSurface, lifecycleGeneration: state.lifecycleGeneration + 1 }),

    beginSidebarRefresh: (state, action: PayloadAction<{ matterId: string; requestId: string }>) => {
      const { matterId, requestId } = action.payload
      const previous = state.sidebarListsByMatterId[matterId]
      state.sidebarListsByMatterId[matterId] = {
        ids: previous?.ids ?? [],
        loaded: previous?.loaded ?? false,
        lastRefresh: previous?.lastRefresh ?? null,
        loading: true,
        error: false,
        requestId,
      }
    },
    finishSidebarRefresh: (state, action: PayloadAction<{ matterId: string; conversations?: AgentV2ConversationSummary[] }>) => {
      const list = state.sidebarListsByMatterId[action.payload.matterId]
      if (!list) return
      list.loading = false
      list.error = !action.payload.conversations
      if (!action.payload.conversations) return
      list.ids = action.payload.conversations
        .filter((c) => state.deletionStatusByConversationId[c.id] !== AgentV2ConversationDeletionStatus.DELETED)
        .map((c) => c.id)
      list.loaded = true
      list.lastRefresh = Date.now()
      for (const conversation of action.payload.conversations) {
        if (state.deletionStatusByConversationId[conversation.id] === AgentV2ConversationDeletionStatus.DELETED) continue
        const existing = state.conversations[conversation.id]
        state.conversations[conversation.id] = { ...existing, ...conversation, lastRefresh: existing?.lastRefresh ?? null }
      }
    },
    setFullListLoaded: (state, action: PayloadAction<{ matterId: string; loaded: boolean }>) => {
      state.fullListLoadedByMatterId[action.payload.matterId] = action.payload.loaded
    },
    beginListRefresh: (state, action: PayloadAction<{ matterId: string; requestId: string; all?: boolean }>) => {
      if (action.payload.all) state.allRequestedByMatterId[action.payload.matterId] = true
      state.requestIdByMatterId[action.payload.matterId] = action.payload.requestId
      state.loadingByMatterId[action.payload.matterId] = true
      state.errorByMatterId[action.payload.matterId] = false
    },
    beginDetailRefresh: (state, action: PayloadAction<{ conversationId: string; requestId: string }>) => {
      if (state.deletionStatusByConversationId[action.payload.conversationId]) return
      state.detailRequestIdByConversationId[action.payload.conversationId] = action.payload.requestId
      state.detailLoadingByConversationId[action.payload.conversationId] = true
      state.detailErrorByConversationId[action.payload.conversationId] = null
    },
    setListLoading: (state, action: PayloadAction<{ matterId: string; loading: boolean }>) => {
      state.loadingByMatterId[action.payload.matterId] = action.payload.loading
    },
    setListError: (state, action: PayloadAction<{ matterId: string; error: boolean }>) => {
      state.errorByMatterId[action.payload.matterId] = action.payload.error
    },
    setInitialLoadComplete: (state, action: PayloadAction<{ matterId: string; complete: boolean }>) => {
      state.initialLoadCompleteByMatterId[action.payload.matterId] = action.payload.complete
    },
    setListLastRefresh: (state, action: PayloadAction<{ matterId: string; lastRefresh: number | null }>) => {
      state.lastRefreshByMatterId[action.payload.matterId] = action.payload.lastRefresh
    },
    upsertConversationFromSummary: (state, action: PayloadAction<AgentV2ConversationSummary>) => {
      if (state.deletionStatusByConversationId[action.payload.id]) return
      state.conversations[action.payload.id] = mergeConversationSummary(state.conversations[action.payload.id], action.payload, null)

      const sidebar = state.sidebarListsByMatterId[action.payload.matter_id]
      if (sidebar && !sidebar.ids.includes(action.payload.id)) sidebar.ids.push(action.payload.id)
      const listedConversationIds = state.listedConversationIdsByMatterId[action.payload.matter_id] ?? []
      if (!listedConversationIds.includes(action.payload.id)) {
        state.listedConversationIdsByMatterId[action.payload.matter_id] = [...listedConversationIds, action.payload.id]
      }
    },
    replaceConversationList: (state, action: PayloadAction<{ matterId: string; conversations: AgentV2ConversationSummary[] }>) => {
      const conversations = action.payload.conversations.filter(
        (conversation) => state.deletionStatusByConversationId[conversation.id] !== AgentV2ConversationDeletionStatus.DELETED
      )
      state.listedConversationIdsByMatterId[action.payload.matterId] = conversations.map((conversation) => conversation.id)

      // The list endpoint is capped, so replacing visible list membership must not purge entities or
      // detail data for omitted conversations that may still be loadable directly.
      for (const conversation of conversations) {
        state.conversations[conversation.id] = mergeConversationSummary(state.conversations[conversation.id], conversation, null)
      }
    },
    conversationDeletionStarted: (state, action: PayloadAction<{ conversationId: string }>) => {
      const history = state.historyByConversationId[action.payload.conversationId]
      if (history) {
        history.requestId = null
        history.loading = false
      }
      state.deletionStatusByConversationId[action.payload.conversationId] = AgentV2ConversationDeletionStatus.DELETING
      delete state.detailRequestIdByConversationId[action.payload.conversationId]
      delete state.detailLoadingByConversationId[action.payload.conversationId]
      delete state.detailErrorByConversationId[action.payload.conversationId]
    },
    conversationDeletionFailed: (state, action: PayloadAction<{ conversationId: string }>) => {
      if (state.deletionStatusByConversationId[action.payload.conversationId] === AgentV2ConversationDeletionStatus.DELETING) {
        delete state.deletionStatusByConversationId[action.payload.conversationId]
      }
    },
    deleteConversation: (state, action: PayloadAction<{ conversationId: string }>) => {
      state.deletionStatusByConversationId[action.payload.conversationId] = AgentV2ConversationDeletionStatus.DELETED
      for (const [matterId, conversationIds] of Object.entries(state.listedConversationIdsByMatterId)) {
        state.listedConversationIdsByMatterId[matterId] = conversationIds.filter((conversationId) => conversationId !== action.payload.conversationId)
      }
      for (const list of Object.values(state.sidebarListsByMatterId)) list.ids = list.ids.filter((id) => id !== action.payload.conversationId)
      for (const turn of state.details[action.payload.conversationId]?.turns ?? []) {
        delete state.feedbackByTurnId[turn.id]
      }
      delete state.conversations[action.payload.conversationId]
      delete state.details[action.payload.conversationId]
      delete state.historyByConversationId[action.payload.conversationId]
      delete state.detailRequestIdByConversationId[action.payload.conversationId]
      delete state.detailLoadingByConversationId[action.payload.conversationId]
      delete state.detailErrorByConversationId[action.payload.conversationId]
      delete state.detailLastRefreshByConversationId[action.payload.conversationId]
      delete state.runLifecycleByConversationId[action.payload.conversationId]
      delete state.unreadResponseByConversationId[action.payload.conversationId]
      delete state.titlePlaceholderByConversationId[action.payload.conversationId]
      delete state.chatFilesByConversationId[action.payload.conversationId]
      for (const [assistantMessageId, followUpState] of Object.entries(state.followUpQuestionsByAssistantMessageId)) {
        if (followUpState?.conversationId === action.payload.conversationId) delete state.followUpQuestionsByAssistantMessageId[assistantMessageId]
      }
    },
    setConversationLoading: (state, action: PayloadAction<{ conversationId: string; isLoading: boolean }>) => {
      state.detailLoadingByConversationId[action.payload.conversationId] = action.payload.isLoading
    },
    setConversationError: (state, action: PayloadAction<{ conversationId: string; loadError: AgentV2DetailLoadError | null }>) => {
      state.detailErrorByConversationId[action.payload.conversationId] = action.payload.loadError
    },
    setChatFiles: (state, action: PayloadAction<{ conversationId: string; fileIds: string[]; writingSampleFileId: string | null }>) => {
      const { conversationId, fileIds, writingSampleFileId } = action.payload
      state.chatFilesByConversationId[conversationId] = { fileIds: Array.from(new Set(fileIds)), writingSampleFileId }
    },
    removeChatFile: (state, action: PayloadAction<{ conversationId: string; fileId: string }>) => {
      const { conversationId, fileId } = action.payload
      const chatFiles = state.chatFilesByConversationId[conversationId]
      if (!chatFiles) return
      chatFiles.fileIds = chatFiles.fileIds.filter((id) => id !== fileId)
    },
    clearChatWritingSample: (state, action: PayloadAction<{ conversationId: string }>) => {
      const chatFiles = state.chatFilesByConversationId[action.payload.conversationId]
      if (chatFiles) chatFiles.writingSampleFileId = null
    },
    setConversationLastRefresh: (state, action: PayloadAction<{ conversationId: string; lastRefresh: number | null }>) => {
      state.detailLastRefreshByConversationId[action.payload.conversationId] = action.payload.lastRefresh
      const conversation = state.conversations[action.payload.conversationId]
      if (conversation) conversation.lastRefresh = action.payload.lastRefresh
    },
    setConversationDetail: (state, action: PayloadAction<AgentV2ConversationDetailResponse>) => {
      const detail = action.payload
      if (state.deletionStatusByConversationId[detail.conversation.id]) return
      for (const turn of detail.turns) {
        state.feedbackByTurnId[turn.id] = turn.feedback
      }
      const existing = state.details[detail.conversation.id]
      state.details[detail.conversation.id] = mergeConversationDetail(existing, detail)
      if (!existing?.turns.length) {
        state.historyByConversationId[detail.conversation.id] = { nextCursor: detail.next_cursor ?? null, loading: false, error: false, requestId: null }
      }
      state.detailLoadingByConversationId[detail.conversation.id] = false
      state.detailErrorByConversationId[detail.conversation.id] = null
      state.conversations[detail.conversation.id] = mergeConversationSummary(state.conversations[detail.conversation.id], detail.conversation, Date.now())
      reconcileRunLifecycleFromDetail(state, detail)
    },
    /** The server admitted an export, so track it before the conversation's next read lists it. */
    docxExportAdmitted: (state, action: PayloadAction<{ conversationId: string; docxExport: DocxExportRead }>) => {
      const { conversationId, docxExport } = action.payload
      const detail = state.details[conversationId]
      if (!detail || !kOpenDocxExportStatuses.has(docxExport.status)) return
      const openExports = detail.open_docx_exports ?? []
      if (openExports.some((openExport) => openExport.id === docxExport.id)) return
      detail.open_docx_exports = [...openExports, docxExport]
    },
    setTurnFeedback: (state, action: PayloadAction<{ feedback: AgentV2TurnFeedback }>) => {
      state.feedbackByTurnId[action.payload.feedback.turn_id] = action.payload.feedback
    },
    setFollowUpQuestionsState: (
      state,
      action: PayloadAction<{
        assistantMessageId: string
        conversationId: string
        loading?: boolean
        isError?: boolean
        questions?: string[]
      }>
    ) => {
      const existing = state.followUpQuestionsByAssistantMessageId[action.payload.assistantMessageId]
      state.followUpQuestionsByAssistantMessageId[action.payload.assistantMessageId] = {
        conversationId: action.payload.conversationId,
        loading: action.payload.loading ?? existing?.loading ?? false,
        isError: action.payload.isError ?? existing?.isError ?? false,
        questions: action.payload.questions ?? existing?.questions ?? [],
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setActiveConversationId, (state, action) => {
        if (action.payload.conversationId) delete state.unreadResponseByConversationId[action.payload.conversationId]
      })
      // A deleted file can't be sent again, so it leaves every chat's files, as it does when a chat loads
      .addCase(UserFilesActions.markUserFilesUnavailable, (state, action) => {
        const deletedFileIds = new Set(action.payload)
        for (const chatFiles of Object.values(state.chatFilesByConversationId)) {
          if (!chatFiles) continue
          chatFiles.fileIds = chatFiles.fileIds.filter((fileId) => !deletedFileIds.has(fileId))
          if (chatFiles.writingSampleFileId && deletedFileIds.has(chatFiles.writingSampleFileId)) chatFiles.writingSampleFileId = null
        }
      })
      .addCase(UserFilesActions.upsertUserFiles, (state, action) => removeChatFilesOutsideMatter(state, action.payload))
      .addCase(UserFilesActions.fetchFilesSucceeded, (state, action) => removeChatFilesOutsideMatter(state, action.payload.files))
  },
})

/** A chat can only send its own matter's files, so a file moved to another matter leaves that chat's files. */
function removeChatFilesOutsideMatter(state: AgentV2ConversationsState, files: UserFileRead[]) {
  for (const [conversationId, chatFiles] of Object.entries(state.chatFilesByConversationId)) {
    const matterId = state.conversations[conversationId]?.matter_id
    if (!chatFiles || !matterId) continue
    const movedFileIds = new Set(files.filter((file) => file.matter_id !== matterId).map((file) => file.id))
    if (movedFileIds.size === 0) continue
    chatFiles.fileIds = chatFiles.fileIds.filter((fileId) => !movedFileIds.has(fileId))
    if (chatFiles.writingSampleFileId && movedFileIds.has(chatFiles.writingSampleFileId)) chatFiles.writingSampleFileId = null
  }
}

export const AgentV2ConversationsActions = agentV2ConversationsSlice.actions

export default agentV2ConversationsSlice.reducer

/**
 * Keeps activity the client already knows about, since a response requested before a turn was admitted still says the
 * conversation has none. The later `updated_at` is kept too, which may be the browser's time from when a turn was admitted.
 */
function mergeConversationSummary(
  existing: AgentV2Conversation | undefined,
  incoming: AgentV2ConversationSummary,
  fallbackLastRefresh: number | null
): AgentV2Conversation {
  const keepsExistingUpdatedAt = existing !== undefined && Date.parse(existing.updated_at) > Date.parse(incoming.updated_at)
  return {
    ...existing,
    ...incoming,
    has_activity: incoming.has_activity || Boolean(existing?.has_activity),
    updated_at: keepsExistingUpdatedAt ? existing.updated_at : incoming.updated_at,
    lastRefresh: existing?.lastRefresh ?? fallbackLastRefresh,
  }
}

/** Marks an admitted turn as activity straight away, and moves the conversation to the top of its list. */
function markConversationActive(state: AgentV2ConversationsState, conversationId: string) {
  const conversation = state.conversations[conversationId]
  if (!conversation) return
  conversation.has_activity = true
  conversation.updated_at = new Date().toISOString()
}

function createRunLifecycle(phase: AgentV2RunPhase, recoveryGeneration: number): AgentV2RunLifecycle {
  return {
    phase,
    turnId: null,
    clientRequestId: null,
    pendingRequest: null,
    admissionFallbackInFlight: false,
    stopRequested: false,
    recoveryGeneration,
  }
}

function startRunRecovery(state: AgentV2ConversationsState, conversationId: string) {
  const existing = state.runLifecycleByConversationId[conversationId]
  if (existing?.phase === AgentV2RunPhase.RECOVERING) return

  const latestTurn = getLatestTurn(state.details[conversationId])
  const knownTurn = existing?.turnId ? state.details[conversationId]?.turns.find((turn) => turn.id === existing.turnId) : undefined
  const recoveredTurn = knownTurn ?? (latestTurn?.ended_at === null ? latestTurn : undefined)
  state.runLifecycleByConversationId[conversationId] = {
    ...createRunLifecycle(AgentV2RunPhase.RECOVERING, (existing?.recoveryGeneration ?? 0) + 1),
    turnId: existing?.turnId ?? recoveredTurn?.id ?? null,
    clientRequestId: existing?.clientRequestId ?? recoveredTurn?.client_request_id ?? null,
    pendingRequest: existing?.pendingRequest ?? null,
    stopRequested: existing?.stopRequested ?? false,
  }
}

function reconcileRunLifecycleFromDetail(state: AgentV2ConversationsState, detail: AgentV2ConversationDetailResponse) {
  const lifecycle = state.runLifecycleByConversationId[detail.conversation.id]
  if (!lifecycle) return

  if (lifecycle.phase === AgentV2RunPhase.RUNNING || lifecycle.phase === AgentV2RunPhase.STOPPING) {
    const liveTurn = detail.turns.find((turn) => turn.id === lifecycle.turnId)
    if (liveTurn?.ended_at !== null && liveTurn?.ended_at !== undefined) {
      markRunLifecycleIdle(lifecycle)
    }
    return
  }

  if (lifecycle.phase !== AgentV2RunPhase.RECOVERING) return

  const awaitedTurn = lifecycle.turnId
    ? detail.turns.find((turn) => turn.id === lifecycle.turnId)
    : lifecycle.clientRequestId
    ? detail.turns.find((turn) => turn.client_request_id === lifecycle.clientRequestId)
    : undefined

  if (!awaitedTurn) return
  lifecycle.turnId = awaitedTurn.id
  lifecycle.clientRequestId = awaitedTurn.client_request_id
  lifecycle.pendingRequest = null
  lifecycle.admissionFallbackInFlight = false
  if (awaitedTurn.ended_at) markRunLifecycleIdle(lifecycle)
}

function markRunLifecycleIdle(lifecycle: AgentV2RunLifecycle) {
  lifecycle.phase = AgentV2RunPhase.IDLE
  lifecycle.stopRequested = false
}

/** The latest turn the user started; server-written notice turns (assistant-only) are skipped. */
export function getLatestTurn(detail: AgentV2ConversationDetailResponse | undefined) {
  if (!detail) return undefined
  return [...detail.turns]
    .filter((turn) => turn.events.length === 0 || turn.events.some((event) => event.type === AgentV2ServerEventType.USER_MESSAGE_CREATED))
    .sort((a, b) => Date.parse(a.started_at) - Date.parse(b.started_at))
    .at(-1)
}
